import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import lifactorLogo from "@/assets/lifactor-logo-white.png";
import testimonialDirector from "@/assets/testimonial-director.jpg";
import testimonialHr from "@/assets/testimonial-hr.jpg";
import testimonialOwner from "@/assets/testimonial-owner.jpg";
import testimonialMarketing from "@/assets/testimonial-marketing.jpg";
import { useHeadlineVariant } from "@/hooks/use-headline-variant";
import { supabase } from "@/integrations/supabase/client";
import { ReportFooter } from "@/components/ReportFooter";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LeadForm, LeadFormData } from "@/components/LeadForm";
import { useState } from "react";
import { toast } from "sonner";
import { useLanguageWithOverride } from "@/hooks/use-browser-language";
import { getHeroTranslation } from "@/i18n/hero";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const Hero = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguageWithOverride();
  const t = getHeroTranslation(language);
  
  const {
    headline,
    subtitle,
    buttonText,
    variantId,
    isLoading
  } = useHeadlineVariant();
  
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const toggleLanguage = () => {
    const newLang = language === "pl" ? "en" : "pl";
    setLanguage(newLang);
    // Update URL to persist language
    const url = new URL(window.location.href);
    url.searchParams.set('lang', newLang);
    window.history.replaceState({}, '', url.toString());
  };
  
  const handleLeadFormSubmit = async (data: LeadFormData) => {
    setIsSubmitting(true);
    
    try {
      const { data: result, error } = await supabase.functions.invoke('submit-lead', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          consent: data.consent,
          marketing: data.marketing
        }
      });
      
      if (error) throw error;
      
      if (result?.success) {
        toast.success(t("toastSuccess"));
        setIsLeadDialogOpen(false);
        navigate('/dziekujemy');
      } else {
        throw new Error(result?.error || 'Error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error(t("toastError"));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const features = [
    { icon: "⚡", text: t("feature1") },
    { icon: "🎯", text: t("feature2") },
    { icon: "📊", text: t("feature3") },
    { icon: "🔓", text: t("feature4") },
  ];
  
  const testimonials = [
    {
      image: testimonialHr,
      name: t("testimonial1Name"),
      position: t("testimonial1Position"),
      quote: t("testimonial1Quote")
    },
    {
      image: testimonialOwner,
      name: t("testimonial2Name"),
      position: t("testimonial2Position"),
      quote: t("testimonial2Quote")
    },
    {
      image: testimonialDirector,
      name: t("testimonial3Name"),
      position: t("testimonial3Position"),
      quote: t("testimonial3Quote")
    },
    {
      image: testimonialMarketing,
      name: t("testimonial4Name"),
      position: t("testimonial4Position"),
      quote: t("testimonial4Quote")
    }
  ];
  
  return <>
    <section className="min-h-screen flex flex-col gradient-hero relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
      <div className="absolute h-full w-full bg-gradient-to-b from-transparent via-transparent to-background/50" />
      
      {/* Logo Header */}
      <div className="container relative z-10 px-4 pt-8 pb-4">
        <div className="text-center">
          <img 
            src={lifactorLogo} 
            alt="Lifactor" 
            className="w-40 md:w-48 mx-auto"
            width="158"
            height="40"
          />
          <p className="text-white/80 text-sm md:text-base font-medium mt-2">
            {t("tagline")}
          </p>
        </div>
      </div>
      
      {/* Hero Content */}
      <div className="flex-1 flex items-center justify-center">
        <div className="container relative z-10 px-4 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
              <CheckCircle2 className="h-4 w-4" />
              {t("badge")}
            </span>
          </div>
          
          {headline ? (
            <h1 className="text-4xl md:text-6xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {headline}
            </h1>
          ) : (
            <Skeleton className="h-16 md:h-20 w-full max-w-3xl mx-auto mb-6 bg-white/20" />
          )}
          
          {subtitle ? (
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">{subtitle}</p>
          ) : (
            <Skeleton className="h-8 w-full max-w-2xl mx-auto mb-8 bg-white/20" />
          )}
          
          <div className="flex flex-col gap-4 justify-center items-center mb-12 px-4">
            <Button 
              size="lg" 
              onClick={() => {
                // Track CTA click in database
                const sessionId = localStorage.getItem('headline_session_id');
                if (sessionId) {
                  supabase
                    .from('headline_assignments')
                    .update({ cta_clicked: true, cta_clicked_at: new Date().toISOString() })
                    .eq('session_id', sessionId)
                    .then(() => console.log('CTA click tracked'));
                }
                
                // Track CTA click with Facebook Pixel
                if (typeof window !== 'undefined' && window.fbq) {
                  window.fbq('trackCustom', 'QuizCTAClicked', {
                    button_location: 'hero',
                    button_text: buttonText,
                    variant_id: variantId
                  });
                }
                navigate(`/quiz?lang=${language}`, {
                  state: {
                    continue: false
                  }
                });
              }} 
              className="bg-white text-primary hover:bg-white shadow-xl hover:shadow-2xl shadow-black/20 hover:shadow-primary/30 text-lg px-8 sm:px-10 py-3 h-auto group flex-col transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:-translate-y-0.5 w-full max-w-md"
            >
              {buttonText ? (
                <>
                  <span className="flex items-center">
                    {buttonText}
                    <ArrowRight className="ml-2 h-5 w-5 transition-all duration-300 group-hover:translate-x-2" />
                  </span>
                  <span className="text-xs text-primary/70 font-normal mt-1">{t("ctaSubtext")}</span>
                </>
              ) : (
                <Skeleton className="h-6 w-48 bg-white/20" />
              )}
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {features.map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-4 text-white border border-white/20">
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-lg font-medium">{item.text}</div>
              </div>
            ))}
          </div>

          {/* Testimonials Section */}
          <div className="mt-16 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-blue-900/40 via-blue-800/30 to-indigo-900/40 backdrop-blur-md p-6 shadow-xl border border-white/10 flex flex-col h-full">
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
                    <Quote className="h-6 w-6 text-white/40 mb-3 flex-shrink-0" />
                    <p className="text-white/95 italic leading-relaxed">&quot;{testimonial.quote}&quot;</p>
                  </div>
                  <div className="flex gap-4 items-center pt-4 border-t border-white/10">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="w-14 h-14 rounded-full object-cover shadow-lg ring-2 ring-white/20 flex-shrink-0"
                      width="56"
                      height="56"
                      loading="lazy"
                    />
                    <div className="text-left">
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-white/70 text-sm">{testimonial.position}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Secondary CTA - Lead Form (above footer, visible on all devices) */}
      <div className="relative z-10 py-12 md:py-16">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
        
        <div className="container relative px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            {/* Glowing card container */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/40 via-white/20 to-primary/40 rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
              <Button 
                variant="outline"
                size="lg" 
                onClick={() => setIsLeadDialogOpen(true)}
                className="relative bg-white/10 backdrop-blur-md text-white border-white/40 hover:bg-white/20 hover:border-white/60 shadow-2xl hover:shadow-primary/20 px-6 sm:px-8 py-8 h-auto w-full transition-all duration-500 rounded-xl"
              >
                <span className="flex flex-col items-center justify-center gap-2 text-base sm:text-lg md:text-xl font-medium">
                  <span className="text-center">{t("secondaryCta1")}</span>
                  <span className="text-center flex items-center gap-2 text-white/90">
                    {t("secondaryCta2")}
                    <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 animate-pulse" />
                  </span>
                </span>
              </Button>
            </div>
            <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-xl mx-auto leading-relaxed px-2 sm:px-4">
              {t("secondaryCtaDesc")}
            </p>
          </div>
        </div>
      </div>
      
      {/* Lead Form Dialog */}
      <Dialog open={isLeadDialogOpen} onOpenChange={setIsLeadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t("dialogTitle")}</DialogTitle>
          </DialogHeader>
          <LeadForm onSubmit={handleLeadFormSubmit} isSubmitting={isSubmitting} />
        </DialogContent>
      </Dialog>
    </section>

    {/* Footer with Language Switcher */}
    <ReportFooter 
      languageSwitcher={
        <LanguageSwitcher 
          language={language} 
          onToggle={toggleLanguage}
          className="border-none pt-0"
        />
      }
    />
  </>;
};
